<template>
    <div>
        <b-modal
            id="treatementBundle"
            ref="treatementBundle"
            :lazy="true"
            :title="title"
            no-close-on-backdrop
            title-tag="h2"
            @shown="shown"
            @hidden="hidden"
            content-class="overflow-visible"
            body-class="overflow-visible"
        >
            <InvoiceInPaymentWarning
                :invoiceId="data.bill_to_invoice"
                module="medical-record"
            />
            <b-form v-if="page === 'start'">
                <b-alert
                    show
                    variant="warning"
                    v-if="data.isDefaultProvider && data.template_id"
                >
                    <span id="dialog-alert-message">
                        {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY_BUNDLE }}
                    </span>
                </b-alert>
                <b-form-group
                    label="Patient:"
                    label-cols="3"
                    label-for="patient_id"
                >
                    <v-select
                        v-if="!disablePatientField"
                        id="patient_id"
                        ref="vselect"
                        v-model="data.patient"
                        v-validate="'required'"
                        :options="pOptions"
                        :class="{
                            'border-danger': errors.has('patient_id'),
                            rounded: true
                        }"
                        placeholder="Patient Name (required)"
                        name="patient_id"
                        label="name"
                        @input="patientChange"
                    />
                    <p v-else class="plain-text">
                        {{ data.patient ? data.patient.name : null }}
                    </p>
                </b-form-group>
                <b-form-group
                    :label-for="`template_id`"
                    :label-cols="3"
                    label="Template:"
                >
                    <v-select
                        id="template_id"
                        ref="vselect"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('template_id') }"
                        :options="bundleFilter"
                        v-model="data.template_id"
                        :label="'name'"
                        name="template_id"
                        aria-placeholder="true"
                        placeholder="Bundle template (required)"
                    ></v-select>
                </b-form-group>
                <b-form-group
                    label="Invoice:"
                    class
                    label-cols="3"
                    label-for="bill_to_invoice"
                >
                    <b-form-select
                        id="bill_to_invoice"
                        v-model="data.bill_to_invoice"
                        :disabled="data.patient === null"
                        :options="invoices"
                        name="bill_to_invoice"
                        class="form-control"
                    >
                        <option :value="-1">New Invoice</option>
                    </b-form-select>
                </b-form-group>
                <b-form-group
                    label="Provider:"
                    class
                    label-cols="3"
                    label-for="staff_id"
                >
                    <v-select
                        id="staffs"
                        ref="vselect"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('staffs') }"
                        :options="staffList"
                        v-model="data.staff_id"
                        :label="'name'"
                        name="staffs"
                        aria-placeholder="true"
                        placeholder="Provider (required)"
                    ></v-select>
                </b-form-group>
                <b-form-group
                    class="medical-note"
                    :label-for="`soap`"
                    :label-cols="3"
                    label="Medical Note:"
                >
                    <b-form-select
                        v-if="!disabledSoapChange"
                        id="soap"
                        v-model="data.soap"
                        :options="medicalNotes"
                        name="soap"
                    >
                    </b-form-select>
                    <p v-else class="plain-text">
                        {{ data.soap ? getSoapName(data.soap) : null }}
                    </p>
                </b-form-group>
                <b-form-group
                    :label-for="`start`"
                    :label-cols="3"
                    :label="'Start:'"
                >
                    <vet-datetime
                        id="start"
                        v-model="data.start"
                        v-validate="'required'"
                        :placeholder="{
                            date: 'Date (required)',
                            time: 'Time (required)'
                        }"
                        container="#treatementBundle"
                        rules="required"
                        name="start"
                        format="DateTime"
                    ></vet-datetime>
                </b-form-group>
            </b-form>
            <b-form v-else-if="page === 'activity'">
                <b-row class="w-100 ml-0" align-v="start">
                    <div>
                        <b-form-checkbox
                            v-model="selectedAll"
                            class="align-items-start"
                            plain
                            :disabled="!_.some(templates)"
                            @change="selectAll"
                        >
                            <strong>Select / Unselect All</strong>
                        </b-form-checkbox>
                    </div>
                </b-row>
                <hr class="mb-0" />
                <item-rows
                    v-for="template in templates"
                    :key="template.id"
                    :element="template"
                    :patient-id="patientID"
                    :templates="templates"
                    @update-selected-all="updateSelectedAll"
                    @update-new-templates="
                        newTemplates => (templates = newTemplates)
                    "
                />
                <div v-if="!_.some(templates)" class="pt-3">
                    <em>No activity defined in the template</em>
                </div>
                <hr v-if="!_.some(templates)" class="mb-0" />
            </b-form>
            <b-form v-else-if="page === 'bundle'">
                <bundle
                    ref="bundle"
                    v-if="page === 'bundle'"
                    :data="bundle_data"
                    :app-data="data"
                    @loading="loading = true"
                    @loaded="bundleLoaded"
                    class="px-1"
                />
            </b-form>
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading"
                    class="float-left"
                    variant="secondary"
                    @click="$refs.treatementBundle.hide('cancel')"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img v-if="loading" src="assets/ajax-loader.gif" />
                    <b-button
                        v-if="!loading && page !== 'start'"
                        class="float-left mr-2"
                        variant="primary"
                        @click="back"
                        >Back</b-button
                    >
                    <b-button
                        v-if="!loading && page === 'start'"
                        variant="primary"
                        @click="showActivity()"
                        >Next</b-button
                    >
                    <b-button
                        v-if="!loading && page === 'activity' && !saveButton"
                        variant="primary"
                        @click="showBundle()"
                        >Next</b-button
                    >
                    <b-button
                        v-if="(!loading && page === 'bundle') || saveButton"
                        variant="primary"
                        :disabled="isHasUsersChooseInvoice"
                        @click="save()"
                        >Save</b-button
                    >
                </div>
            </div>
        </b-modal>
    </div>
</template>
<style></style>
<script>
import vSelect from "vue-select";
import ItemRows from "./template-item";
import Bundle from "@/module/commons/bundle/bundle";
import moment from "moment";
import uuidv4 from "uuid/v4";
import ScheduleHelper from "../helper";
import { DateTime } from "luxon";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import CLINICPROVIDER from "@/constants/clinic-provider";
import InvoiceInPaymentWarning from "@/components/invoice/invoice-in-payment-warning";
import joinPaymentInvoiceSocket from "@/mixins/join-payment-invoice-socket.js";
import {
    orderBy,
    forEach,
    cloneDeep,
    get,
    set,
    union,
    find,
    times,
    filter,
    isEmpty,
    some
} from "lodash";

// Constant for readability
const DUE_AT_SPECIFIC_TIME = 2;

export default {
    name: "TreatementBundle",
    components: {
        vSelect,
        ItemRows,
        Bundle,
        vetDatetime,
        InvoiceInPaymentWarning
    },
    mixins: [ScheduleHelper, joinPaymentInvoiceSocket],
    data() {
        return {
            search_text: "",
            continues: false,
            loading: false,
            page: "start",
            saveButton: false,
            checker: true,
            title: "Add Bundle",
            selectedAll: true,
            patientID: "",
            clientID: "",
            data: {
                patient: null,
                template_id: null,
                bill_to_invoice: -1,
                staff_id: "",
                soap: -1,
                start: DateTime.local(),
                time: DateTime.local(),
                invoice_id: "",
                weight: 0,
                uom: null,
                isDefaultProvider: false
            },
            bundle_data: {
                bundle_id: null,
                selectedElements: [],
                elements: [],
                module: ""
            },
            datePickerOptions: {
                format: "DD/MM/YYYY"
            },
            type: [
                {
                    value: null,
                    text: "Select type of activity (required)"
                },
                {
                    value: 1,
                    text: "Medical"
                },
                {
                    value: 2,
                    text: "Vitals"
                },
                {
                    value: 3,
                    text: "Other"
                }
            ],
            treatmentData: [],
            treatmentID: "",
            pOptions: [],
            bundles: [],
            invoices: [],
            staffList: [],
            medicalNotes: [
                {
                    value: -1,
                    text: "No Medical Note"
                }
            ],
            templates: [],
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT,
            disablePatientField: false,
            treatmentBoardPatient: null,
            disabledSoapChange: false
        };
    },
    computed: {
        bundleFilter() {
            return [
                ...this.bundles.map(bundle => ({
                    id: bundle.id,
                    name: bundle.name
                }))
            ];
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        },
        isHasUsersChooseInvoice() {
            return this.data.bill_to_invoice
                ? !isEmpty(
                      this.$store.getters["billing/findUserChooseInvoice"](
                          this.data.bill_to_invoice
                      )
                  )
                : false;
        }
    },
    mounted() {},
    beforeRouteEnter(to, from, next) {
        next(self => {
            if (to.params.patient) {
                self.disablePatientField = true;
                self.treatmentBoardPatient = to.params.patient;
            }
            self.$refs["treatementBundle"].show();
        });
    },
    watch: {
        "data.patient"(value) {
            if (value) {
                this.loading = true;
                this.treatmentID = value.treatment_board_id;
                this.patientID = value.id;
                this.clientID = value.client_id;
                this.getBilling(value.client_id);
                this.getMedicalNote(value.id);
                if (value.appointment_id !== null) {
                    this._getAppointment(value.appointment_id).then(
                        appointment => {
                            let currentStaff = _.find(this.staffList, [
                                "id",
                                appointment.provider.id
                            ]);
                            this.data.staff_id = currentStaff;
                        }
                    );
                }
                const vitals = get(
                    this.$store.state.patients,
                    this.patientID + ".vitals",
                    null
                );
                let weight = find(vitals, { type: 4 });
                if (weight) {
                    this.data.weight = weight.value;
                    this.data.uom = weight.uom_id;
                }
            } else {
                this.data.bill_to_invoice = -1;
                this.medicalNotes = [
                    {
                        value: -1,
                        text: "No Medical Note"
                    }
                ];
                this.data.soap = -1;
                this.data.weight = 0;
                this.data.uom = null;
            }
        },
        "data.staff_id"(value) {
            if (value !== "") {
                if (this.staffList.length === 0) {
                    this.data.staff_id = "";
                } else {
                    this.setStaffId(this.staffList);
                }
            }
        },
        "data.bill_to_invoice"(value) {
            if (value !== -1) {
                this.joinPaymentInvoice(value);
            }
        }
    },
    methods: {
        onSearch(search) {
            this.remote_options = [];
            this.search_text = search;
            if (search.length >= 2) {
                this.loading = true;
                this.filterOptions(search);
            }
        },
        setSearchText(event) {
            this.txt = this.search_text;
            this.$emit("search:blur", event);
        },
        open(params) {
            let self = this;
            if (params.patient) {
                self.disablePatientField = true;
                self.treatmentBoardPatient = params.patient;
            }
            self.disabledSoapChange = params.disabledSoapChange;
            self.$refs["treatementBundle"].show();
        },
        shown() {
            this.loading = true;
            this.datePickerOptions.format = this.getDateFormat();
            this.data.start = DateTime.local().setZone(this._timeZone);
            $(".modal-content").removeAttr("tabindex");
            Promise.all([
                this.getPatient(),
                this.getBundle(),
                this.getStaffList()
            ]).then(() => {
                this.loading = false;
            });
        },
        hidden() {
            if (this.$route.path === "/schedule/treatment/treatment-bundle") {
                this.$router.replace("/schedule/treatment");
            }
        },
        selectAll() {
            this.templates = this.templates.map(template => {
                if (some(template.medications)) {
                    if (!this.selectedAll) {
                        if (template.originalQuantity) {
                            template.medications[0].quantity =
                                template.originalQuantity;
                        }
                    } else {
                        template.originalQuantity =
                            template.medications[0].quantity;
                        template.medications[0].quantity = 0;
                    }
                }
                return {
                    ...template,
                    selected: !this.selectedAll
                };
            });
        },
        updateSelectedAll() {
            this.selectedAll = !this.templates.find(
                template => !template.selected
            );
        },
        getBundle() {
            let self = this;
            return this.$http
                .get(
                    process.env.ACCOUNT_SERVICE + "template/bundle?allBundle=1"
                )
                .then(function (response) {
                    if (response.data.response.message.code == 200) {
                        self.bundles = response.data.response.resources;
                        self.data.template_id = self.bundles[0];
                    } else {
                        self.isLoading = false;
                        self._message(
                            "Error : Unable to get bundle list",
                            "danger"
                        );
                    }
                });
        },
        async getBilling(clientID) {
            let self = this;
            return await this.$http
                .get(
                    process.env.ACTOR_SERVICE +
                        "client/" +
                        clientID +
                        "/invoice"
                )
                .then(function (response) {
                    if (response.data.response.resource) {
                        self.invoices = union(
                            response.data.response.resource.map(invoice => ({
                                value: invoice.id,
                                text:
                                    "Invoice #" +
                                    invoice.index +
                                    " (" +
                                    moment(invoice.start.date).format(
                                        self.datePickerOptions.format
                                    ) +
                                    ")"
                            }))
                        );
                        self.data.bill_to_invoice =
                            self.invoices.length > 0
                                ? self.invoices[0].value
                                : -1;
                    }
                });
        },
        getPatient() {
            let updatedLink = this.disablePatientField
                ? `schedule/treatment-board-patient?patient_id=${this.treatmentBoardPatient.id}`
                : "schedule/treatment-board-patient";
            return this.$http
                .get(process.env.BARRAMUNDI + updatedLink)
                .then(response => {
                    this.pOptions = response.data.response.resources.map(
                        patient => ({
                            id: patient.patient_id,
                            appointment_id: patient.appointment_id,
                            client_id: patient.client_id,
                            treatment_board_id: patient.id,
                            name:
                                patient.patient.name +
                                " " +
                                (patient.actor?.lastname ?? "")
                        })
                    );
                })
                .finally(() => {
                    if (this.treatmentBoardPatient) {
                        this.data.patient =
                            this.pOptions.find(
                                patient =>
                                    patient.id === this.treatmentBoardPatient.id
                            ) || null;
                    }
                });
        },
        getTreatmentBoard() {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "schedule/treatment-board/" +
                        self.treatmentID
                )
                .then(function (response) {
                    if (response.data.response.resources) {
                        self.treatmentData = response.data.response.resources;
                        let soap =
                            parseInt(self.treatmentData.soap_id) === -1 ||
                            self.treatmentData.soap_id === null
                                ? -1
                                : self.treatmentData.soap_id;
                        self.data.soap = find(self.medicalNotes, {
                            value: soap
                        })
                            ? soap
                            : -1;

                        self.loading = false;
                    }
                });
        },
        getStaffList() {
            let self = this;

            return this.$http
                .get(
                    process.env.APPS +
                        "index.php/ajax/staff/filter_administrator_veterinarian_sql?search_key="
                )
                .then(function (response) {
                    if (response.data) {
                        self.staffList = response.data;
                        self.staffList = _.sortBy(self.staffList, ["name"]);

                        if (self.staffList.length === 0) {
                            self.data.staff_id = "";
                        } else {
                            self.setStaffId(self.staffList);
                        }
                    }
                    return self.staffList;
                });
        },
        setStaffId(providerOptions) {
            let self = this;
            let existingStaff = find(providerOptions, [
                "id",
                self.data.staff_id.id
            ]);
            let defaultClinicProvider = find(providerOptions, [
                "is_default_provider",
                true
            ]);

            if (!existingStaff) {
                let currentStaff = find(providerOptions, [
                    "id",
                    this.$root.currentActor.id
                ]);
                // default the provider if cannot find provider
                if (defaultClinicProvider && !currentStaff) {
                    self.data.isDefaultProvider = true;
                    self.data.staff_id = defaultClinicProvider;
                } else {
                    self.data.staff_id = currentStaff
                        ? currentStaff
                        : providerOptions[0];
                }
            } else {
                if (defaultClinicProvider) {
                    self.data.isDefaultProvider =
                        self.data.staff_id == defaultClinicProvider;
                }
            }
        },
        getMedicalNote(patientID) {
            let self = this;
            let medicalNote = [];
            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "record/medical-note-list/" +
                        patientID
                )
                .then(function (response) {
                    if (response.data.response.resources) {
                        forEach(response.data.response.resources, function (
                            value
                        ) {
                            let name = "";
                            switch (value.type) {
                                case 0:
                                case 1:
                                case 2:
                                    name =
                                        "Medical Note" +
                                        " (" +
                                        self
                                            ._toDateTime(value.date)
                                            .toFormat(self._dateFormat) +
                                        ")";
                                    break;
                                default:
                                    if (value.template_id) {
                                        name =
                                            value.template_name +
                                            " (" +
                                            self
                                                ._toDateTime(value.date)
                                                .toFormat(self._dateFormat) +
                                            ")";
                                    }
                            }
                            if (value.title) {
                                name =
                                    value.title +
                                    " (" +
                                    self
                                        ._toDateTime(value.date)
                                        .toFormat(self._dateFormat) +
                                    ")";
                            }

                            medicalNote.push({
                                value: value.id,
                                text: name
                            });
                        });

                        self.medicalNotes = union(
                            [{ value: -1, text: "No Medical Note" }],
                            medicalNote
                        );

                        self.getTreatmentBoard();
                    }
                });
        },
        patientChange(patient) {
            let self = this;
            self.data.patient_id = patient ? patient.id : null;
            this.patient_id = self.data.patient_id;
        },
        async getActivities() {
            let self = this;
            await self.$http
                .get(
                    process.env.BARRAMUNDI +
                        "schedule/treatment-template-activities/" +
                        self.data.template_id.id
                )
                .then(response => {
                    self.templates = self.generateActivities(
                        response.data.response.resources
                    );
                    self.loading = false;
                    return self.templates;
                });
        },
        generateActivities(templates) {
            let occurence_array = [];
            let labelOptions = this.labelOptions;

            templates.forEach(template => {
                if (get(template, "activity.repeat.occurence") > 1) {
                    template.id = uuidv4();
                    template.occurrence_id = template.id;
                    times(get(template, "activity.repeat.occurence"), time => {
                        if (time < 1) {
                            return;
                        }
                        let interval = get(
                            template,
                            "activity.repeat.specific_interval"
                        );
                        let object = cloneDeep(template);

                        // -- assign temporary uniqid
                        object.id = uuidv4();
                        if (template.activity.due.type === 1) {
                            set(
                                object,
                                "activity.due.after_start_time",
                                get(template, "activity.due.after_start_time") +
                                    interval * time
                            );
                        } else {
                            set(
                                object,
                                "activity.due.specific_time",
                                DateTime.fromFormat(
                                    get(template, "activity.due.specific_time"),
                                    "hh:mm a"
                                )
                                    .plus({
                                        minutes: interval * time
                                    })
                                    .toSeconds()
                            );
                        }
                        occurence_array.push(object);
                    });
                }
            });
            return orderBy(
                this.addOneDay(
                    union(templates, occurence_array).map(template => ({
                        id: template.id,
                        occurrence_id: template.occurrence_id,
                        selected: true,
                        treatment_board_id: this.treatmentID,
                        treatment_activity_type_id: template.activity_type,
                        repeat_data: template.activity.repeat,
                        due: this._calculateDue(
                            this.data.start,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            this._now()
                        ).diff,
                        due_type: template.activity.due.type,
                        time: this._calculateDue(
                            this.data.start,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            this._now()
                        ).time,
                        note: "",
                        startNow: this.data.start,
                        start: this.treatmentData.start,
                        medications: template.activity.item_id
                            ? [
                                  {
                                      item_id: template.item.id,
                                      name: template.item.name,
                                      uom: template.item.uom,
                                      status: 0,
                                      quantity: template.activity.quantity,
                                      route_id: template.activity.route_id,
                                      location_id:
                                          template.activity.location_id,
                                      template_element_id:
                                          template.templateElement[0].id
                                  }
                              ]
                            : undefined,
                        otherTasks: [
                            {
                                label_id: get(template, "activity.label_id", 1),
                                taskType:
                                    template.activity_type === 3
                                        ? find(labelOptions, {
                                              value: parseInt(
                                                  template.activity.label_id
                                              )
                                          }).text
                                        : null,
                                task: template.activity.task,
                                status: 0
                            }
                        ],
                        vitals: template.master_vital
                            ? template.master_vital.map(item => {
                                  let imperial = this._setting.weights_measures;
                                  let masterUom = [];
                                  switch (parseInt(item.id)) {
                                      case 3:
                                          masterUom = find(item.master_uom, {
                                              uom: "F"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "C"
                                                  }
                                              );
                                          break;
                                      case 4:
                                          masterUom = find(item.master_uom, {
                                              uom: "Lbs"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "Kg"
                                                  }
                                              );
                                          break;
                                      default:
                                          masterUom = item.master_uom[0];
                                          break;
                                  }

                                  return {
                                      master_vital_id: item.id,
                                      name: item.name,
                                      value: null,
                                      patient_id: this.data.patient,
                                      master_uom_id: masterUom
                                          ? masterUom.id
                                          : 0,
                                      status: 0
                                  };
                              })
                            : undefined
                    }))
                ),
                "time",
                "asc"
            );
        },
        /**
         * Ensure every activity’s `due` / `time` is not before `now`.
         * If the first occurrence in a chain moves, its children move too.
         * Mutates the objects in-place and also returns the array for chaining.
         */
        addOneDay(activities) {
            const tz = this._timeZone; // grab once
            const now = this._now(); // Luxon DateTime in the same zone
            const bumpedSet = new Set(); // parents that were shifted

            // helper: return epoch, shifted +1 day if in the past
            const shiftIfPast = epoch => {
                const dt = DateTime.fromSeconds(epoch).setZone(tz);
                return dt < now ? dt.plus({ days: 1 }).toSeconds() : epoch;
            };

            return activities.map(act => {
                const isParent = act.id === act.occurrence_id;
                const parentBump = bumpedSet.has(act.occurrence_id);

                //------------------------------------------
                // Case A – This is the parent in the chain
                //------------------------------------------
                if (act.occurrence_id && isParent) {
                    const oldDue = act.due;
                    const oldTime = act.time;

                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = shiftIfPast(act.due);
                    }
                    act.time = shiftIfPast(act.time);

                    // Record that this chain must be shifted
                    if (oldDue !== act.due || oldTime !== act.time) {
                        bumpedSet.add(act.occurrence_id);
                    }
                    return act;
                }

                //------------------------------------------------------
                // Case B – Child occurrence whose parent was bumped
                //------------------------------------------------------
                if (act.occurrence_id && parentBump) {
                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = DateTime.fromSeconds(act.due)
                            .setZone(tz)
                            .plus({ days: 1 })
                            .toSeconds();
                    }
                    act.time = DateTime.fromSeconds(act.time)
                        .setZone(tz)
                        .plus({ days: 1 })
                        .toSeconds();
                    return act;
                }

                //------------------------------------------
                // Case C – Not recurring OR parent untouched
                //------------------------------------------
                if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                    act.due = shiftIfPast(act.due);
                }
                act.time = shiftIfPast(act.time);
                return act;
            });
        },
        async showActivity() {
            let valid = await this.$validator.validateAll();
            if (valid) {
                $(".modal-content").removeAttr("tabindex");

                this.templates = [];
                this.loading = true;
                await this.getActivities();
                this.bundleChecker();
            }
        },
        showBundle() {
            let self = this;
            self.checker = false;
            self.bundle_data.bundle_id = this.data.template_id.id;
            self.bundle_data.module = "treatmentboard";
            self.page = "bundle";
            self.title = "New Bundle";
            $(".modal-content").removeAttr("tabindex");
        },
        bundleChecker() {
            let self = this;
            self.checker = true;
            self.bundle_data.bundle_id = this.data.template_id.id;
            self.bundle_data.module = "treatmentboard";
            self.page = "bundle";
        },
        bundleLoaded(value) {
            let self = this;
            self.loading = false;

            if (this.checker) {
                if (value) {
                    if (this.templates.length == 0) {
                        this.showBundle();
                    } else {
                        this.page = "activity";
                        this.title = "New Activity";
                        this.saveButton = false;
                        $(".modal-content").removeAttr("tabindex");
                    }
                } else {
                    this.page = "activity";
                    this.title = "New Activity";
                    this.saveButton = true;
                    $(".modal-content").removeAttr("tabindex");
                }
            }
        },
        back() {
            this.saveButton = false;
            if (this.page === "bundle") {
                if (!_.some(this.templates)) {
                    this.page = "start";
                    this.title = "Add Bundle";
                } else {
                    this.page = "activity";
                    this.title = "New Activity";
                }
            } else if (this.page === "activity") {
                this.page = "start";
                this.title = "Add Bundle";
            }
        },
        async billingSoapSave() {
            let self = this;
            let bs_response = false;
            self.loading = true;
            self.saveButton = false;

            // create invoice/estimate by billing_to_invoice
            if (self.data.bill_to_invoice === -1) {
                let status = 1;

                let billingData = {
                    cid: self.clientID,
                    client: "Fake data",
                    pid: self.data.patient.id,
                    location_id: 1,
                    start: self.data.time.toFormat(self._dateFormat),
                    start_time: self.data.time.toFormat("hh:mm a"),
                    expire: self.data.time
                        .plus({ days: 14 })
                        .toFormat(self._dateFormat),
                    soap_id: self.data.soap,
                    status: status,
                    title: "Invoice",
                    template: 0
                };

                let billingResponse = await $.post(
                    process.env.APPS + "index.php/billing/invoice/save",
                    billingData
                );

                if (billingResponse.plain_id) {
                    if (billingData.status === 1) {
                        self.data.invoice_id = billingResponse.plain_id;
                    }
                }
                bs_response = true;
            } else {
                self.data.invoice_id = self.data.bill_to_invoice;
            }
            return bs_response;
        },
        async save_bundle(post, bundle_data) {
            if (bundle_data.selectedElements) {
                bundle_data.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;
                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }

                    if (item.tag_number) {
                        post["fm_bundle_element_tag_number_" + item.id] =
                            item.tag_number;
                    }
                });
            }
            return await $.post(
                process.env.APPS + "index.php/patient/procedure/save",
                post
            );
        },
        async save() {
            let self = this;
            let response = false;
            let valid = await self.$validator.validateAll();

            if (valid) {
                let bs_response = await this.billingSoapSave();
                let params = {
                    patient_id: self.data.patient.id,
                    client_id: self.clientID,
                    template_id: self.data.template_id.id,
                    status_id: self.TREATMENT_STATUS.TREATMENT,
                    invoice_id: self.data.invoice_id
                        ? self.data.invoice_id
                        : null,
                    soap_id: self.data.soap !== -1 ? self.data.soap : null,
                    activities: filter(self.templates, { selected: true })
                };

                try {
                    if (params) {
                        response = await this.$http
                            .put(
                                process.env.BARRAMUNDI +
                                    "schedule/treatment-board/" +
                                    self.treatmentID,
                                params
                            )
                            .then(async response => {
                                if (response.status === 200) {
                                    if (
                                        self.bundle_data.selectedElements
                                            .length > 0
                                    ) {
                                        let post = {
                                            pid: self.data.patient.id,
                                            bid: self.data.staff_id.id,
                                            sid:
                                                self.data.soap !== -1
                                                    ? self.data.soap
                                                    : null,

                                            template: self.data.template_id,
                                            bill_to_invoice:
                                                response.data.response.resources
                                                    .invoice_id,
                                            start: self.data.time.toFormat(
                                                self._dateFormat
                                            ),
                                            start_time: self.data.time.toFormat(
                                                "hh:mm a"
                                            )
                                        };
                                        let bundleResponse = [];
                                        bundleResponse = await self.save_bundle(
                                            post,
                                            self.bundle_data
                                        );

                                        if (
                                            bundleResponse.accession_id &&
                                            bundleResponse.antech_token
                                        ) {
                                            self.$root.$emit(
                                                "call-antech-iframe",
                                                {
                                                    antech_token:
                                                        bundleResponse.antech_token,
                                                    accession_id:
                                                        bundleResponse.accession_id
                                                }
                                            );
                                        } else if (
                                            (bundleResponse.lab_ids &&
                                                bundleResponse.lab_ids.length >
                                                    0) ||
                                            bundleResponse.idexx_lab_id
                                        ) {
                                            let print_lab_params = {
                                                idexx_lab_id:
                                                    bundleResponse.idexx_lab_id,
                                                idexx_url:
                                                    bundleResponse.idexx_url,
                                                lab_ids: bundleResponse.lab_ids,
                                                source:
                                                    bundleResponse.lab_source,
                                                module: "appointment"
                                            };

                                            self._print_lab_order(
                                                print_lab_params
                                            ).then(function (lab_response) {
                                                if (lab_response.idexx_iframe) {
                                                    //load idexx iframe
                                                    lab_response.module_url =
                                                        print_lab_params.module_url;
                                                    lab_response.module =
                                                        print_lab_params.module;

                                                    self.$root.$emit(
                                                        // eslint-disable-next-line vue/custom-event-name-casing
                                                        "call_idexx_iframe",
                                                        lab_response
                                                    );
                                                } else {
                                                    //load requisition form modal
                                                    lab_response.module_url =
                                                        print_lab_params.module_url;
                                                    lab_response.module =
                                                        print_lab_params.module;
                                                    self.$root.$emit(
                                                        "call-multirequsition-modal",
                                                        lab_response
                                                    );
                                                }
                                            });
                                        }
                                    }

                                    self.loading = false;
                                    self.$refs.treatementBundle.hide();
                                    self.$root.$emit(
                                        "treatment-board-refresh",
                                        {
                                            params: response.data.response,
                                            treatment_board_id:
                                                response.data.response.resources
                                                    .id
                                        }
                                    );

                                    if (bs_response) {
                                        self.$socket.client.emit(
                                            "v-broadcast-all",
                                            {
                                                action: "refresh-document",
                                                patient_id:
                                                    self.data.patient.id,
                                                businessId:
                                                    self._setting.business_id
                                            }
                                        );
                                    }

                                    self._message(
                                        "Activity created",
                                        "success"
                                    );
                                } else {
                                    self.loading = false;
                                    self._message(
                                        "Error saving data",
                                        "danger"
                                    );
                                }
                            });
                    }
                } catch (errors) {
                    self.loading = false;
                    self._message("Error saving data", "danger");
                }
            }
        },
        getSoapName(id) {
            let medicalNote = this.medicalNotes.find(note => note.value == id);
            return medicalNote ? medicalNote.text : null;
        }
    }
};
</script>
